import { useToggle } from '@vueuse/core';
import { acceptHMRUpdate, defineStore } from 'pinia';
import type { UserResponse } from '~/types/ecommerce';

export const useUserStore = defineStore('user', () => {
  const authStore = useAuthStore();
  const { notify } = useQuasar();
  const { $ecommerce } = useNuxtApp();
  const [visible, toggle] = useToggle();

  const { data, status, execute, refresh, clear } = useAsyncData(
    'user',
    () => $ecommerce.fetch<UserResponse>('users/me')
      .catch((error) => {
        handleFetchError(error);
        return undefined;
      }),
    {
      immediate: authStore.isLoggedIn,
    },
  );

  watch(
    () => authStore.isLoggedIn,
    async (isLoggedIn) => {
      // Clear user data if logged out
      if (!isLoggedIn) {
        clear();
        visible.value = false;
        return;
      }

      // Fetch user data if missing
      if (!data.value) {
        await execute();
      }
    },
  );

  watch(data, (newValue, oldValue) => {
    if (newValue && !oldValue) {
      notify({
        type: 'positive',
        message: authStore.isLoggedIn
          ? `Hi there, ${newValue.firstName}! Welcome back to KYGUNCO.`
          : `Welcome, ${newValue.firstName}. Thank you for signing up for KYGUNCO!`,
      });
    }
  });

  return {
    pending: computed(() => status.value == 'pending'),
    data,
    refresh,
    visible,
    toggle,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
